import type { PageHelp } from "../types";

export const settingsPageHelp: Record<string, PageHelp> = {
  agents: {
    title: "Agents",
    summary: "Your AI workforce: named agents that draft work from your agency's data. Drafts only; people send.",
    steps: [
      { title: "Open an agent and press Chat", body: "Pick an account if you want it to use that account's details, then type your request." },
      { title: "Switch an agent on to schedule it", body: "Edit the agent, choose a schedule, and turn the switch on. It runs once a day and notifies you." },
      { title: "Check the Runs & timing tab", body: "See which model handled each run, how long it took, and what it wrote." },
    ],
    tips: [
      "Agents use Gemini's free tier unless you pick another model for them.",
      "Start a message with local: or gemini: to force a model for that one request.",
    ],
    articles: ["agents-overview"],
  },

  settings: {
    title: "Settings",
    summary:
      "Settings is where you connect Flowio to your tools and shape it for your agency. It is split into tabs: Data & Sync, Fields, Content, Scripts, Team, Permissions, Templates, Automations, Content blocks, AI spend and Agency.",
    steps: [
      { title: "Start on Data & Sync", body: "Paste your GoHighLevel token and location ID, add a pipeline, then run Sync from GoHighLevel." },
      { title: "Add your team", body: "Open the Team tab to add people and give each a role." },
      { title: "Check what each role can do", body: "Open the Permissions tab to see or change a role's access." },
      { title: "Set your goals and branding", body: "Open the Content tab for your brand color, MRR goal and target rates." },
      { title: "Customize scripts and messages", body: "Use Scripts, Templates and Content blocks to edit what closers and setters read and send." },
    ],
    tips: [
      "Hover or tap the small (i) icons next to any setting for a short explanation.",
      "Some tabs need owner or admin access. If a control is greyed out, you probably lack permission.",
      "Most fields on the Content tab save automatically when you click out of them.",
    ],
    articles: ["settings-connect-ghl", "settings-api-keys", "settings-team-and-roles"],
  },

  "settings-data": {
    title: "Data & Sync",
    summary:
      "Connects Flowio to GoHighLevel (GHL) and other services, and controls whether Flowio shows demo data or your real synced accounts.",
    steps: [
      { title: "Set the GoHighLevel token", body: "In the GoHighLevel connection card, click Set token, paste a Private Integration token, and click Save." },
      { title: "Enter the GoHighLevel location ID", body: "Paste your GHL sub-account ID and click Save." },
      { title: "Add a pipeline", body: "Under Add a pipeline, click a pipeline name. Open it to set its switches and map each stage to a Flowio status." },
      { title: "Sync your accounts", body: "Click Sync from GoHighLevel. Then switch the Demo / Live toggle at the top to Live to see your real data." },
      { title: "Sync bookings", body: "In Calendar sync, choose a calendar (or All calendars) and click Sync calendar. It then keeps itself in sync every 5 minutes." },
      { title: "Classify your calendars", body: "In Calendar purpose, mark each calendar Sales, Onboarding or Other. Only Sales bookings count toward Sets, Show rate and the funnel." },
      { title: "Add optional keys", body: "Add the AI provider, Email provider, Google Places, Fathom, HeyGen and Cloudflare keys only for the features you use, and set your Timezone." },
    ],
    tips: [
      "Sync from GoHighLevel and Sync calendar stay disabled until a token is saved.",
      "Clear live data deletes all synced live accounts without asking first. Use it only to re-sync from scratch.",
      "Keys are hidden after saving. Use the eye icon to reveal one, or Replace and Clear to change it.",
      "Set your Timezone so 'today' on the scoreboard and reports matches your working day.",
    ],
    articles: ["settings-connect-ghl", "settings-api-keys", "settings-data-sync", "automation-lead-automation", "automation-account-cleanup"],
  },

  "settings-fields": {
    title: "Fields",
    summary:
      "Controls how GHL custom fields appear on account pages: their label, section, type, order and whether they are visible. Fields appear here after the first sync.",
    steps: [
      { title: "Sync first", body: "If the tab says no fields are registered, run Sync from GoHighLevel on the Data & Sync tab." },
      { title: "Rename a field", body: "Type a friendly name in the Label column and click away to save." },
      { title: "Group it", body: "Change the Section column to move the field under a different heading." },
      { title: "Set the order", body: "Enter a number in Order. Lower numbers appear first within the section." },
      { title: "Show or hide", body: "Untick Visible to hide a field, or tick On card to show it on the compact account card." },
      { title: "Hide empty fields", body: "Use the checkbox at the top to hide fields that have no value on an account page." },
    ],
    tips: [
      "Removing a field only removes its configuration in Flowio. The data stays in GHL.",
      "Type changes how a value is shown (for example currency or phone), not what is stored.",
      "The hide-empty checkbox applies to the whole agency and needs agency-settings permission.",
    ],
    articles: ["settings-fields"],
  },

  "settings-content": {
    title: "Content settings",
    summary:
      "Your agency's identity, revenue goal, target rates, Today page shortcuts, live call cockpit options, research model, revenue gates and clocks.",
    steps: [
      { title: "Set identity", body: "Fill in Agency subtitle, SMS signature and Brand color. Brand color recolors buttons and navigation." },
      { title: "Set your goal", body: "Enter MRR goal ($), Goal deadline and Day one. The Command Center uses them for its dial and countdown." },
      { title: "Set targets", body: "Enter a Close-rate target (%) and Show-rate target (%) to show benchmarks on Today and tracking pages." },
      { title: "Add quick actions", body: "Under Quick actions (Today page), click Add action, name it and pick the module it opens." },
      { title: "Tune the call cockpit", body: "Set Target call length (min), Demo line and the scorecard items." },
      { title: "Add revenue gates and clocks", body: "Use Add gate for milestones and Add clock for external waits. Both show on the Command Center." },
    ],
    tips: [
      "Changes save automatically when you click out of a field.",
      "You need permission to edit agency settings; otherwise the fields are read-only.",
      "Pre-call research: Opus is deeper but slower and costs more than Sonnet.",
    ],
    articles: ["settings-content"],
  },

  "settings-scripts": {
    title: "Scripts",
    summary:
      "A visual editor for what closers and setters read: the Live Call sheet, Objections and Setter scripts.",
    steps: [
      { title: "Pick what to edit", body: "Click Live Call sheet, Objections or Setter scripts at the top." },
      { title: "Import the built-ins", body: "If nothing custom exists, click the Import button to copy the current built-in content into editable entries." },
      { title: "Open an entry", body: "Click an entry's name to expand it and edit its lines, beats or phases." },
      { title: "Save", body: "Click Save on that entry. It goes live on the next page load." },
      { title: "Reorder or add", body: "Use the up and down arrows to reorder, or Add to create a new entry." },
    ],
    tips: [
      "Once any custom entry exists, custom content replaces the built-in version for that script type.",
      "Revert to built-in deletes all custom entries of that type after confirmation.",
      "Tokens like {{ownerName}} and {{repName}} fill in live during a call.",
    ],
    articles: ["settings-scripts"],
  },

  "settings-team": {
    title: "Team",
    summary:
      "Manage who has access to this agency, what role each person has, and any pending invitations.",
    steps: [
      { title: "Enter an email", body: "In the Members card, type the person's email address." },
      { title: "Choose a role", body: "Pick a role from the dropdown next to the email." },
      { title: "Add them", body: "Click Add now for instant access, or Send invite link to create a link you copy and send yourself." },
      { title: "Change a role later", body: "Use the role dropdown on the person's row." },
      { title: "Fine-tune access", body: "Click the key icon on a row to give that one person permission exceptions." },
    ],
    tips: [
      "Only admins and owners see these controls. Only an owner can grant the Owner role.",
      "Remove takes effect immediately with no confirmation.",
      "Invite links appear under Pending invites; Revoke cancels one.",
    ],
    articles: ["settings-team-and-roles", "settings-permissions"],
  },

  "settings-permissions": {
    title: "Permissions",
    summary:
      "Decide exactly what each role can do. Every module has sections, and each section allows create, read, update and delete actions.",
    steps: [
      { title: "Pick a role", body: "Click a role in the Roles list on the left." },
      { title: "Expand a module", body: "Click a module name to see its sections and action columns." },
      { title: "Toggle actions", body: "Click a cell to allow (check mark) or deny (dash) that action. A dot marks a change from the role's defaults." },
      { title: "Save", body: "Click Save changes. The change applies to everyone with that role straight away." },
      { title: "Create a custom role", body: "Click the plus button, name the role, choose Starts from, then Create role and tune its cells." },
    ],
    tips: [
      "Read means see, Create means add, Update means edit, Delete means remove.",
      "A personal exception (set from the Team tab) overrides the role for one person only.",
      "Only owners can edit. Reset to defaults discards a role's saved changes.",
      "Some Admin cells are locked so an admin can never lock themselves out.",
    ],
    articles: ["settings-permissions", "settings-team-and-roles"],
  },

  "settings-templates": {
    title: "Templates",
    summary:
      "Reusable message text for notes, SMS and email, with placeholders that fill in from the account when used.",
    steps: [
      { title: "Start a template", body: "Click New template." },
      { title: "Choose the kind", body: "Pick Note, SMS or Email. Email templates also get a Subject." },
      { title: "Name it", body: "Give it a clear name; this is required." },
      { title: "Write the body", body: "Type the message. Use placeholders such as {{first_name}} or {{account_name}}." },
      { title: "Save", body: "Click Save template. Use Edit or the bin icon on a row to change or delete it later." },
    ],
    tips: [
      "Available placeholders are listed at the top of the tab, plus any {{cf_*}} GHL field.",
      "Name and Body are required to save.",
    ],
    articles: ["settings-templates"],
  },

  "settings-automations": {
    title: "Automations",
    summary:
      "Rules that run on their own: when an event happens (trigger) and optional conditions match, Flowio performs one or more actions.",
    steps: [
      { title: "Create a rule", body: "Click New rule and give it a Name." },
      { title: "Pick the trigger", body: "In When, choose an event such as Call logged, Booking confirmed, Booking no-show, Portal form submitted or Checklist stalled." },
      { title: "Add conditions", body: "Under Only when, narrow it (for example Outcome = lost). Leave fields on Any to fire every time." },
      { title: "Add actions", body: "Under Then do, use + Add action: Add note, Notify team, Tag GHL contact, Create checklist or Schedule action." },
      { title: "Save and check", body: "Click Save rule, then watch Recent runs to see whether it fired." },
    ],
    tips: [
      "Use the switch on a rule to turn it off without deleting it.",
      "Schedule action runs another action a set number of days later.",
      "Placeholders like {{account.name}} and {{event.outcome}} fill in when the rule runs.",
      "Changing the When event clears your conditions.",
    ],
    articles: ["settings-automation-rules"],
  },

  "settings-blocks": {
    title: "Content blocks",
    summary:
      "Editable content that feeds other pages, such as Drill Range personas, Playbook phases, Objection responses and the Precall checklist.",
    steps: [
      { title: "Choose a type", body: "Use the dropdown to pick Personas, Playbook phases, Objection responses or Precall checklist." },
      { title: "Get started", body: "Click Seed starters for a small default set, or New to write your own." },
      { title: "Fill in the block", body: "Enter a unique Key and an Order, then complete the fields." },
      { title: "Save", body: "Click Save block. Use Edit or the bin icon on a row later." },
    ],
    tips: [
      "For Playbook phases, Copy the built-in playbook gives you an editable starting point.",
      "New agencies start with no blocks, so pages use built-in content until you add some.",
      "Setter scripts you add fully replace the bundled example.",
      "The Scripts tab is a friendlier editor for the same call sheet, objection and setter content.",
    ],
    articles: ["settings-content-blocks"],
  },

  "settings-ai-spend": {
    title: "AI spend",
    summary:
      "Shows how much your agency's AI features have cost, from a usage ledger you can filter.",
    steps: [
      { title: "Open the tab", body: "Click AI spend in the Settings tab bar." },
      { title: "Filter", body: "Use the filters to narrow the usage by what you want to inspect." },
      { title: "Review the estimates", body: "Read the cost estimates against the provider key you set on Data & Sync." },
    ],
    tips: [
      "Amounts are estimates, so confirm exact billing with your AI provider.",
      "Choosing a deeper model, such as Opus for pre-call research, usually raises cost.",
    ],
    articles: ["automation-ai-spend"],
  },

  "settings-agency": {
    title: "Agency",
    summary:
      "Details about the agency you are working in, plus creating another agency and, for owners, deleting this one.",
    steps: [
      { title: "Review this agency", body: "Check the Name, Your role and Agency ID shown in the This agency card." },
      { title: "Create another agency", body: "Type a name under Create another agency and click Create, if creation is allowed on your deployment." },
      { title: "Delete an agency (owners only)", body: "In the Danger zone, type the agency's exact name, then click Delete agency." },
    ],
    tips: [
      "Each agency is fully isolated, with its own accounts, team and settings.",
      "Deleting is permanent and removes accounts, bookings, calls, checklists and more. It cannot be undone.",
      "If creation is disabled, an operator has to allow your email on this deployment.",
    ],
    articles: ["settings-agency"],
  },
};
